import React, { useState, useEffect, useRef, Suspense } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import DynamicTabs from '../../common/DynamicTabs';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DynamicDrawer from '../../common/Drawer';
import CustomSpeedDial from '../../common/SpeedDial';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import DynamicSnackbar from '../../common/Snackbar';
import auditService from '../../../services/AuditService';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    Pending as PendingIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Dashboard as DashboardIcon,
    Description as DescriptionIcon,
    Assignment as AssignmentIcon,
    Schedule as ScheduleIcon,
    Attachment as AttachmentIcon,
    FolderOpen as FolderOpenIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';  
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import {
    GridToolbar,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarColumnsButton,
    GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import { ViewColumn as ViewColumnIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { Download as DownloadIcon } from '@mui/icons-material';
import roleService from '../../../services/RoleService';
import { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';

const AuditRequests = () => {
    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('All');
    const [open, setOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newRequest, setNewRequest] = useState({
        AUDIT_FILE: '',
        SYSTEM_NAME: '',
        REQUEST_TYPE: '',
        DESCRIPTION: '',
        PHASE: 'Design',
        STATUS: 'Pending',
        PRIORITY: 'Low',
        DUE_DATE: '',
        ASSIGNEE: '',
        COMMENTS: ''
    });
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);
    const { id } = useParams();
    const [selectedProject, setSelectedProject] = useState([]);
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState([]);
    const [appID, setAppID] = useState([]);
    const [companyID, setCompanyID] = useState([]);
    const [controlOptions, setControlOptions] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [clientUsers, setClientUsers] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedTab, setSelectedTab] = useState(0);
    const [csvFile, setCsvFile] = useState(null);
    const [csvError, setCsvError] = useState(null);
    const [searchParams] = useSearchParams();
    const controlFilter = searchParams.get('control');

    const navigate = useNavigate();

    useEffect(() => {
        fetchAuditRequests();
        fetchProjectData();
        fetchCurrentUser();
        fetchClientUsers();
    }, [id, controlFilter]);

    useEffect(() => {
        filterRequests(selectedFilter);
    }, [requests, selectedFilter, controlFilter]);

    const fetchCurrentUser = async () => {
        const currentUser = await userService.fetchCurrentUser();
        if (currentUser) {
            setCurrentUser(currentUser);
        }
    }

    const fetchProjectData = async () => {
        try {
            setLoading(true);
            const projectRecord = await auditService.fetchProjectsById(id);
            if (projectRecord) {
                setSelectedProject(projectRecord);
                setCompanyID(projectRecord.COMPANY_ID);
                
                const appRecord = await appService.fetchAppsByCompany(projectRecord.COMPANY_ID);
                if (appRecord) {
                    const appList = appRecord.map(app => ({
                        value: app.id,
                        label: app.APP_NAME

                    }));
                    setApps(appList);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setSelectedProject([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await auditService.getAuditRequests({ 
                audit_list: id,
                control_id: controlFilter
            });
            console.log('Audit requests response:', response);
            if (response && Array.isArray(response.data)) {
                setRequests(response.data);
            } else {
                setRequests([]);
                setError('No audit requests found or invalid response format');
            }
        } catch (error) {
            console.error('Error fetching audit requests:', error);
            setError('Failed to fetch audit requests. Please try again later.');
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchClientUsers = async () => {
        try {
            const usersData = await roleService.fetchUsers();
            // Create Sets to track unique emails and names
            const uniqueEmails = new Set();
            const uniqueNames = new Set();
            
            // Filter users and ensure uniqueness
            const clientRoleUsers = usersData
                .filter(user => 
                    user.role.role_name === 'Client Participant' || 
                    user.role.role_name === 'Client Coordinator'
                )
                .reduce((acc, user) => {
                    const email = user.user.email;
                    const fullName = `${user.user.first_name} ${user.user.last_name}`;
                    const role = user.role.role_name;
                    
                    // Only add the user if we haven't seen this email before
                    if (!uniqueEmails.has(email)) {
                        uniqueEmails.add(email);
                        
                        // If we've seen this name before, append email to make it unique
                        let displayName = fullName;
                        if (uniqueNames.has(fullName)) {
                            displayName = `${fullName} (${email})`;
                        }
                        uniqueNames.add(fullName);
                        
                        acc.push({
                            value: email, // Store email as value
                            label: `${displayName} - ${role}`, // Show unique name with role
                            fullName: fullName // Keep original full name for display
                        });
                    }
                    return acc;
                }, []);

            // Sort users alphabetically by full name
            clientRoleUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));
            
            setClientUsers(clientRoleUsers);
        } catch (error) {
            console.error('Error fetching client users:', error);
        }
    };

    const filterRequests = (status) => {
        if (!Array.isArray(requests)) return;
        
        let filtered = [...requests];
        
        console.log('Initial requests:', requests);
        console.log('Control filter:', controlFilter);
        
        // First apply control filter if present
        if (controlFilter) {
            filtered = filtered.filter(request => {
                const matches = request.audit_file_details?.CONTROL_ID === controlFilter;
                console.log('Checking request:', request.audit_file_details?.CONTROL_ID, 'against', controlFilter, 'Result:', matches);
                return matches;
            });
        }
        
        console.log('After control filter:', filtered);
        
        // Then apply status filter
        if (status !== 'All') {
            filtered = filtered.filter(request => request.STATUS === status);
            console.log('After status filter:', filtered);
        }
        
        setFilteredRequests(filtered);
    };

    const handleFilterClick = (status) => {
        setSelectedFilter(status === selectedFilter ? 'All' : status);
    };

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const droppedFiles = Array.from(event.dataTransfer.files);
        setFiles(prevFiles => [...prevFiles, ...droppedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const handleCsvUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            setCsvError('Please upload a valid CSV file');
            return;
        }

        setCsvFile(file);
        setCsvError(null);
    };

    const handleBulkUpload = async () => {
        try {
            if (!csvFile) {
                setCsvError('Please select a CSV file');
                return;
            }

            setLoading(true);
            const formData = new FormData();
            formData.append('csv_file', csvFile);
            
            const user = `${currentUser.first_name} ${currentUser.last_name}`;
            formData.append('CREATED_BY', user);
            formData.append('UPDATED_BY', user);

            const response = await auditService.createBulkAuditRequest(formData);
            
            await fetchAuditRequests();
            
            setSnackbarMessage('Bulk requests created successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setOpen(false);
            
            // Reset form
            setCsvFile(null);
            setCsvError(null);
            setSelectedTab(0);
        } catch (error) {
            console.error('Error creating bulk requests:', error);
            setSnackbarMessage(error.response?.data?.detail || 'Error creating bulk requests');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        
        setNewRequest(prev => ({
            ...prev,
            [name]: value
        }));

        // Only fetch controls when the SYSTEM_NAME changes
        if (name === 'SYSTEM_NAME') {
            try {
                await fetchWorkpapers(value, companyID);
            } catch (error) {
                console.error('Error fetching controls:', error);
                setControlOptions([]);
            }
        }
    };

    const handleCreateRequest = async () => {
        try {
            // Validate required fields
            if (!newRequest.DESCRIPTION.trim()) {
                setValidationErrors({ DESCRIPTION: true });
                setSnackbarMessage('Description is required');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            setValidationErrors({});
            setLoading(true);
            
            // Create form data
            const formData = new FormData();
            
            // Add request data
            Object.keys(newRequest).forEach(key => {
                formData.append(key, newRequest[key]);
            });
            
            // Add audit list ID
            formData.append('AUDIT_LIST', id);
            
            const user = `${currentUser.first_name} ${currentUser.last_name}`
            formData.append('CREATED_BY', user);
            formData.append('UPDATED_BY', user);
            
            // Add files
            files.forEach(file => {
                formData.append('files', file);
            });

            const response = await auditService.createAuditRequest(formData);
            
            // Refresh the list after creating new request
            await fetchAuditRequests();
            
            setSnackbarMessage('Request created successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setOpen(false);
            
            // Reset form
            setNewRequest({
                AUDIT_FILE: '',
                SYSTEM_NAME: '',
                REQUEST_TYPE: '',
                DESCRIPTION: '',
                PHASE: 'Design',
                STATUS: 'Pending',
                PRIORITY: 'Low',
                DUE_DATE: '',
                ASSIGNEE: '',
                COMMENTS: ''
            });
            setFiles([]);
            setUploadProgress(0);
        } catch (error) {
            console.error('Error creating request:', error);
            setSnackbarMessage(error.response?.data?.detail || 'Error creating request');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchWorkpapers = async (app_id, company_id) => {
        try {
            // Pass the project ID to fetch only workpapers for this project
            const controls = await auditService.fetchWorkpapersByApp(app_id, company_id, id);
            if (controls) {
                const controlsList = controls.map(ctrl => ({
                    value: ctrl.id,
                    label: ctrl.CONTROL_DETAILS.CONTROL_ID + ' - ' + ctrl.CONTROL_DETAILS.CONTROL_TITLE
                }));
                setControlOptions(controlsList);
            }
        } catch (error) {
            console.error('Failed to fetch workpapers:', error);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Completed':
                return <CheckCircleIcon sx={{ color: 'success.main' }} />;
            case 'Pending':
                return <PendingIcon sx={{ color: 'warning.main' }} />;
            case 'In Review':
                return <AccessTimeIcon sx={{ color: 'info.main' }} />;
            case 'Overdue':
                return <ErrorOutlineIcon sx={{ color: 'error.main' }} />;
            case 'Evidence Received':
                return <AttachFileIcon sx={{ color: '#9c27b0' }} />;
            default:
                return <AssignmentTurnedInIcon />;
        }
    };

    const calculateAging = (dueDate) => {
        if (!dueDate) return 'N/A';
        const today = new Date();
        const due = new Date(dueDate);
        const diffTime = due - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            return `${Math.abs(diffDays)} days overdue`;
        } else if (diffDays === 0) {
            return 'Due today';
        } else {
            return `${diffDays} days left`;
        }
    };

    const statusCounts = React.useMemo(() => {
        if (!Array.isArray(requests)) return {};
        return requests.reduce((acc, request) => {
            if (request && request.STATUS) {
                acc[request.STATUS] = (acc[request.STATUS] || 0) + 1;
            }
            return acc;
        }, {});
    }, [requests]);

    // Memoize form sections to prevent unnecessary re-renders
    const formSections = React.useMemo(() => [
        {
            title: "Basic Information",
            icon: <AssignmentIcon color="primary" />,
            fields: (
                <mui.Grid container spacing={2}>
                    <mui.Grid item xs={12} md={6}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Application"
                            selectOptions={apps}
                            value={newRequest.SYSTEM_NAME}
                            handleChange={(selected) => handleInputChange({ target: { name: 'SYSTEM_NAME', value: selected.value } })}
                            fullWidth
                            size="small"
                            sx={{ minWidth: '100%' }}
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12} md={6}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Control ID"
                            selectOptions={controlOptions}
                            value={newRequest.AUDIT_FILE}
                            handleChange={(selected) => handleInputChange({ target: { name: 'AUDIT_FILE', value: selected.value } })}
                            fullWidth
                            size="small"
                            sx={{ minWidth: '100%' }}
                        />
                    </mui.Grid>
                </mui.Grid>
            )
        },
        {
            title: "Request Details",
            icon: <DescriptionIcon color="primary" />,
            fields: (
                <mui.Grid container spacing={2}>
                    <mui.Grid item xs={12} md={6}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Request Type"
                            selectOptions={[
                                { value: 'Population', label: 'Population' },
                                { value: 'Samples', label: 'Samples' },
                                { value: 'Walkthrough', label: 'Walkthrough' },
                                { value: 'Follow-up', label: 'Follow-up ' },
                                { value: 'Other', label: 'Other' }
                            ]}
                            value={newRequest.REQUEST_TYPE}
                            handleChange={(selected) => handleInputChange({ target: { name: 'REQUEST_TYPE', value: selected.value } })}
                            fullWidth
                            size="small"
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12} md={6}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Phase"
                            selectOptions={[
                                { value: 'Design', label: 'Design' },
                                { value: 'Interim', label: 'Interim' },
                                { value: 'Rollforward', label: 'Rollforward' },
                                { value: 'Final', label: 'Final' }
                            ]}
                            value={newRequest.PHASE}
                            handleChange={(selected) => handleInputChange({ target: { name: 'PHASE', value: selected.value } })}
                            fullWidth
                            size="small"
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12}>
                        <mui.TextField
                            label="Description"
                            name="DESCRIPTION"
                            multiline
                            rows={4}
                            value={newRequest.DESCRIPTION}
                            onChange={handleInputChange}
                            fullWidth
                            variant="outlined"
                            required
                            error={validationErrors.DESCRIPTION}
                            helperText={validationErrors.DESCRIPTION ? 'Description is required' : ''}
                            sx={{ 
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        backgroundColor: '#f0f0f0'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#fff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem'
                                },
                                '& .MuiFormHelperText-root': {
                                    fontSize: '0.75rem'
                                }
                            }}
                        />
                    </mui.Grid>
                </mui.Grid>
            )
        },
        {
            title: "Priority & Timeline",
            icon: <ScheduleIcon color="primary" />,
            fields: (
                <mui.Grid container spacing={2}>
                    <mui.Grid item xs={12} md={6}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Priority"
                            selectOptions={[
                                { value: 'High', label: 'High' },
                                { value: 'Medium', label: 'Medium' },
                                { value: 'Low', label: 'Low' }
                            ]}
                            value={newRequest.PRIORITY}
                            handleChange={(selected) => handleInputChange({ target: { name: 'PRIORITY', value: selected.value } })}
                            fullWidth
                            size="small"
                            sx={{ minWidth: '100%' }}
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12} md={6}>
                        <mui.TextField
                            type="date"
                            label="Due Date"
                            name="DUE_DATE"
                            value={newRequest.DUE_DATE || ''}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                            sx={{ minWidth: '100%' }}
                        />
                    </mui.Grid>
                </mui.Grid>
            )
        },
        {
            title: "Assignment & Comments",
            icon: <AssignmentTurnedInIcon color="primary" />,
            fields: (
                <mui.Grid container spacing={2}>
                    <mui.Grid item xs={12}>
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Assignee"
                            selectOptions={clientUsers}
                            value={newRequest.ASSIGNEE}
                            handleChange={(selected) => handleInputChange({ target: { name: 'ASSIGNEE', value: selected.value } })}
                            fullWidth
                            size="small"
                            sx={{ minWidth: '100%' }}
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12}>
                        <mui.TextField
                            label="Comments"
                            name="COMMENTS"
                            multiline
                            rows={4}
                            value={newRequest.COMMENTS}
                            onChange={handleInputChange}
                            fullWidth
                            variant="outlined"
                            sx={{ 
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        backgroundColor: '#f0f0f0'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#fff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </mui.Grid>
                </mui.Grid>
            )
        }
    ], [apps, controlOptions, newRequest, handleInputChange, clientUsers]); // Add all dependencies

    // Memoize the form content
    const formContent = React.useMemo(() => (
        <mui.Box sx={{ padding: '20px' }}>
            {formSections.map((section, index) => (
                <mui.Paper 
                    key={index} 
                    elevation={0} 
                    sx={{ 
                        p: 2, 
                        mb: 3, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 2,
                        backgroundColor: '#fff'
                    }}
                >
                    <mui.Typography 
                        variant="subtitle1" 
                        gutterBottom 
                        sx={{ 
                            mb: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1 
                        }}
                    >
                        {section.icon}
                        {section.title}
                    </mui.Typography>
                    {section.fields}
                </mui.Paper>
            ))}

            
        </mui.Box>
    ), [formSections]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return { bg: '#4caf5015', color: '#4caf50' };
            case 'Pending': return { bg: '#ff980015', color: '#ff9800' };
            case 'In Review': return { bg: '#03a9f415', color: '#03a9f4' };
            case 'Overdue': return { bg: '#f4433615', color: '#f44336' };
            case 'Evidence Received': return { bg: '#9c27b015', color: '#9c27b0' };
            default: return { bg: '#90909015', color: '#909090' };
        }
    };

    const getPriorityColor = (priority) => {
        return { bg: '#90909015', color: '#909090' };
    };

    const columns = [
        { 
            field: 'REQUEST_ID', 
            headerName: 'ID', 
            flex: 0.5,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <mui.Typography 
                    variant="body2" 
                    sx={{ 
                        fontSize: '0.8125rem',
                        width: '100%',
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                    onClick={() => navigate(`/audit/project/${id}/requests/${params.row.id}`)}
                >
                    {params?.row?.REQUEST_ID || 'N/A'}
                </mui.Typography>
            )
        },
        { 
            field: 'AUDIT_FILE', 
            headerName: 'Control', 
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <mui.Tooltip 
                    title={params?.row?.audit_file_details?.CONTROL_TITLE || 'N/A'} 
                    arrow
                >
                    <mui.Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: '0.8125rem',
                            width: '100%',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        {params?.row?.audit_file_details?.CONTROL_ID || 'N/A'}
                    </mui.Typography>
                </mui.Tooltip>
            )
        },
        { 
            field: 'SYSTEM_NAME',
            headerName: 'System',
            flex: 1,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <mui.Typography 
                    variant="body2" 
                    sx={{ 
                        fontSize: '0.8125rem',
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    {params?.row?.audit_file_details?.APP_NAME || 'N/A'}
                </mui.Typography>
            )
        },
        { 
            field: 'REQUEST_TYPE', 
            headerName: 'Type', 
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <mui.Typography 
                    variant="body2" 
                    sx={{ 
                        fontSize: '0.8125rem',
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    {params?.row?.REQUEST_TYPE || 'N/A'}
                </mui.Typography>
            )
        },
        { 
            field: 'PHASE',
            headerName: 'Phase',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <mui.Typography 
                    variant="body2" 
                    sx={{ 
                        fontSize: '0.8125rem',
                        color: 'text.secondary',
                        fontWeight: 500
                    }}
                >
                    {params?.row?.PHASE || 'N/A'}
                </mui.Typography>
            )
        },
        { 
            field: 'DESCRIPTION',
            headerName: 'Description',
            flex: 2,
            minWidth: 200,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <mui.Tooltip title={params?.row?.DESCRIPTION || 'N/A'} arrow>
                    <mui.Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8125rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params?.row?.DESCRIPTION || 'N/A'}
                    </mui.Typography>
                </mui.Tooltip>
            )
        },
       
        { 
            field: 'STATUS',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const status = params?.row?.STATUS || 'N/A';
                const { color } = getStatusColor(status);
                return (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '100%',
                        gap: 0.5 
                    }}>
                        <mui.Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: color,
                                flexShrink: 0
                            }}
                        />
                        <mui.Typography 
                            variant="body2" 
                            sx={{ 
                                fontSize: '0.8125rem', 
                                color: 'text.secondary'
                            }}
                        >
                            {status}
                        </mui.Typography>
                    </mui.Box>
                );
            }
        },
        { 
            field: 'PRIORITY', 
            headerName: 'Priority', 
            flex: 0.7,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const priority = params?.row?.PRIORITY || 'N/A';
                return (
                    <mui.Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: '0.8125rem',
                            color: 'text.secondary'
                        }}
                    >
                        {priority}
                    </mui.Typography>
                );
            }
        },
        { 
            field: 'ASSIGNEE', 
            headerName: 'Assignee', 
            flex: 1.2,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                // Find the matching client user to get the full name
                const assigneeUser = clientUsers.find(user => user.value === params?.row?.ASSIGNEE);
                const displayName = assigneeUser ? assigneeUser.fullName : (params?.row?.ASSIGNEE || 'Unassigned');
                
                return (
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <mui.Avatar 
                            sx={{ 
                                width: 20, 
                                height: 20, 
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main' 
                            }}
                        >
                            {(displayName || 'U')[0].toUpperCase()}
                        </mui.Avatar>
                        <mui.Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                            {displayName}
                        </mui.Typography>
                    </mui.Box>
                );
            }
        },
        { 
            field: 'DUE_DATE',
            headerName: 'Due Date',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const dueDate = params?.row?.DUE_DATE;
                return (
                    <mui.Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                        {dueDate ? new Date(dueDate).toLocaleDateString() : 'N/A'}
                    </mui.Typography>
                );
            }
        },
        { 
            field: 'AGING',
            headerName: 'Aging',
            flex: 0.8,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const dueDate = params?.row?.DUE_DATE;
                const aging = dueDate ? calculateAging(dueDate) : 'N/A';
                const isOverdue = aging.includes('overdue');
                const isDueToday = aging === 'Due today';
                
                return (
                    <mui.Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: '0.8125rem',
                            color: isOverdue ? 'error.main' : 
                                   isDueToday ? 'warning.main' : 
                                   'text.secondary'
                        }}
                    >
                        {aging}
                    </mui.Typography>
                );
            }
        },
        {
            field: 'HAS_RESPONSE',
            headerName: 'Files',
            flex: 0.5,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const hasComments = params.row.comments && params.row.comments.length > 0;
                // Only count files that are not from the requestor as evidence
                const evidenceFiles = params.row.evidence_files ? 
                    params.row.evidence_files.filter(file => file.uploaded_by !== params.row.CREATED_BY) : [];
                const hasEvidence = evidenceFiles.length > 0;
                const evidenceCount = evidenceFiles.length;
                const hasNewComments = hasComments && params.row.comments.some(comment => !comment.is_read);
                
                return (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        justifyContent: 'center'
                    }}>
                        {hasComments && (
                            <mui.Box sx={{ position: 'relative' }}>
                                <mui.Tooltip title={`${params.row.comments.length} comment(s)${hasNewComments ? ' - New comments!' : ''}`} arrow>
                                    <mui.Icon sx={{ 
                                        color: hasNewComments ? '#1976d2' : '#90a4ae',
                                        fontSize: '1.1rem'
                                    }}>
                                        chat
                                    </mui.Icon>
                                </mui.Tooltip>
                                {hasNewComments && (
                                    <mui.Box
                                        sx={{
                                            position: 'absolute',
                                            top: -2,
                                            right: -2,
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: '#f44336',
                                            border: '1px solid #fff'
                                        }}
                                    />
                                )}
                            </mui.Box>
                        )}
                        {hasEvidence && (
                            <mui.Tooltip title={`${evidenceCount} evidence file(s) attached`} arrow>
                                <mui.Box 
                                    onClick={() => navigate(`/audit/project/${id}/requests/${params.row.id}`)}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <AttachFileIcon sx={{ 
                                        color: '#2e7d32',
                                        fontSize: '1.1rem',
                                        transform: 'rotate(45deg)'
                                    }} />
                                    <mui.Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: '#2e7d32',
                                            fontWeight: 500
                                        }}
                                    >
                                        {evidenceCount}
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Tooltip>
                        )}
                    </mui.Box>
                );
            }
        },
    ];

    const SummaryCard = ({ icon: Icon, title, count, status, color }) => (
        <mui.Card 
            onClick={() => handleFilterClick(status)}
            sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: selectedFilter === status 
                    ? `linear-gradient(135deg, ${color}15, ${color}30)`
                    : 'background.paper',
                border: 'none',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: selectedFilter === status ? 2 : 1,
                    '&::before': {
                        opacity: 1,
                    }
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: color,
                    opacity: selectedFilter === status ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                }
            }}
        >
            <mui.CardContent sx={{ p: 2 }}>
                <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1.5,
                    position: 'relative'
                }}>
                    <mui.Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1.5,
                            p: 0.75,
                            background: `${color}15`,
                            mr: 1.5
                        }}
                    >
                        <Icon sx={{ color: color, fontSize: 20 }} />
                    </mui.Box>
                    <mui.Typography 
                        variant="subtitle2" 
                        sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {title}
                    </mui.Typography>
                </mui.Box>
                <mui.Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <mui.Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 700,
                            color: 'text.primary',
                            fontSize: '1.75rem',
                            lineHeight: 1
                        }}
                    >
                        {count}
                    </mui.Typography>
                    <mui.Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                        }}
                    >
                        requests
                    </mui.Typography>
                </mui.Box>
            </mui.CardContent>
        </mui.Card>
    );

    const CustomGridToolbar = () => {
        return (
            <mui.Box 
                sx={{ 
                    px: 3,
                    py: 2,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <mui.Typography 
                    variant="h6" 
                    sx={{ 
                        fontSize: '0.95rem',
                        fontWeight: 600
                    }}
                >
                    Request Summary {selectedFilter !== 'All' && `- ${selectedFilter}`}
                </mui.Typography>
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1
                    }}
                >
                    {selectedFilter !== 'All' && (
                        <mui.IconButton
                            size="small"
                            onClick={() => setSelectedFilter('All')}
                            sx={{ color: 'text.secondary' }}
                        >
                            <CloseIcon fontSize="small" />
                        </mui.IconButton>
                    )}
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport 
                        printOptions={{ 
                            disableToolbarButton: true
                        }}
                        csvOptions={{
                            fileName: 'audit-requests-export',
                            delimiter: ',',
                            utf8WithBom: true,
                        }}
                    />
                </mui.Box>
            </mui.Box>
        );
    };

    const customMainContent = (
        <ResponsiveContainer>
            <mui.Box sx={{ padding: { xs: '16px', sm: '24px' }, maxWidth: '1600px', margin: '0 auto' }}>
                <mui.Breadcrumbs 
                    aria-label="breadcrumb"
                    sx={{
                        '& .MuiLink-root': {
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: 'primary.main'
                            }
                        }
                    }}
                >
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        <i className="material-icons">home</i>
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        My Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href={`/audit/projects/${selectedProject?.id}`}>
                        {selectedProject?.AUDIT_NAME}
                    </mui.Link>
                    <mui.Typography color="text.primary">Audit Requests</mui.Typography>
                </mui.Breadcrumbs>

                <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    mb: 4, 
                    mt: 3,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 }
                }}>
                    <CloudUploadIcon sx={{ 
                        fontSize: 28, 
                        color: 'primary.main', 
                        mr: { xs: 0, sm: 2.5 },
                        opacity: 0.9
                    }} />
                    <mui.Box sx={{ flex: 1 }}>
                        <mui.Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary', 
                                mb: 1,
                                fontSize: { xs: '1.5rem', sm: '2rem' }
                            }}
                        >
                            Audit Requests
                        </mui.Typography>
                        <mui.Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            flexWrap: 'wrap'
                        }}>
                            <mui.Box sx={{ 
                                display: 'inline-flex', 
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '6px',
                                backgroundColor: getStatusColor('Active').bg,
                                gap: 1
                            }}>
                                <span style={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%',
                                    backgroundColor: getStatusColor('Active').color,
                                    display: 'inline-block'
                                }} />
                                <mui.Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: getStatusColor('Active').color,
                                        fontSize: '0.8125rem',
                                        fontWeight: 500
                                    }}
                                >
                                    Active Project
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>
                </mui.Box>

                <mui.Box sx={{ 
                    backgroundColor: '#f8fafc', 
                    borderRadius: 3,
                    minHeight: '80vh',
                    boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                }}>
                    <mui.Paper 
                        elevation={0}
                        sx={{ 
                            p: 2,
                            height: '100%',
                            backgroundColor: 'transparent'
                        }}
                    >
                        {loading ? (
                            <mui.Box sx={{ width: '100%' }}>
                                <mui.Box 
                                    sx={{ 
                                        mb: 1,
                                        background: 'white',
                                        borderRadius: 2,
                                        p: 2.5,
                                        boxShadow: '0 1px 8px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <mui.Grid container spacing={2}>
                                        {[1, 2, 3, 4, 5].map((item) => (
                                            <mui.Grid item xs={12} sm={6} md={item === 1 ? 3 : item === 5 ? 3 : 2} key={item}>
                                                <mui.Skeleton
                                                    variant="rectangular"
                                                    sx={{
                                                        height: '100px',
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(0,0,0,0.04)',
                                                        transform: 'scale(1)',
                                                        animation: 'pulse 1.5s infinite ease-in-out'
                                                    }}
                                                />
                                            </mui.Grid>
                                        ))}
                                    </mui.Grid>
                                </mui.Box>

                                <mui.Paper 
                                    sx={{ 
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        border: '1px solid #e0e0e0',
                                        overflow: 'hidden',
                                        p: 3
                                    }}
                                >
                                    <mui.Box sx={{ mb: 2 }}>
                                        <mui.Skeleton 
                                            variant="rectangular" 
                                            height={40} 
                                            sx={{ 
                                                borderRadius: 1,
                                                backgroundColor: 'rgba(0,0,0,0.04)',
                                                mb: 2
                                            }} 
                                        />
                                    </mui.Box>
                                    {[1, 2, 3, 4, 5].map((item) => (
                                        <mui.Box key={item} sx={{ mb: 1 }}>
                                            <mui.Skeleton 
                                                variant="rectangular" 
                                                height={52} 
                                                sx={{ 
                                                    borderRadius: 1,
                                                    backgroundColor: 'rgba(0,0,0,0.04)'
                                                }} 
                                            />
                                        </mui.Box>
                                    ))}
                                    <mui.Box 
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            mt: 3 
                                        }}
                                    >
                                        <mui.CircularProgress 
                                            size={40}
                                            thickness={4}
                                            sx={{
                                                color: 'primary.main',
                                                animation: 'fadeIn 0.5s ease-in'
                                            }}
                                        />
                                    </mui.Box>
                                </mui.Paper>

                                <style>
                                    {`
                                        @keyframes pulse {
                                            0% {
                                                opacity: 0.6;
                                            }
                                            50% {
                                                opacity: 0.4;
                                            }
                                            100% {
                                                opacity: 0.6;
                                            }
                                        }
                                        @keyframes fadeIn {
                                            from {
                                                opacity: 0;
                                            }
                                            to {
                                                opacity: 1;
                                            }
                                        }
                                    `}
                                </style>
                            </mui.Box>
                        ) : (
                            <>
                                <mui.Box 
                                    sx={{ 
                                        mb: 3,
                                        background: 'white',
                                        borderRadius: 2,
                                        p: 2.5,
                                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
                                    }}
                                >
                                    <mui.Grid container spacing={2}>
                                        <mui.Grid item xs={12} sm={6} md={3}>
                                            <SummaryCard
                                                icon={DashboardIcon}
                                                title="Total Requests"
                                                count={Array.isArray(requests) ? requests.length : 0}
                                                status="All"
                                                color="#2196f3"
                                            />
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={6} md={2}>
                                            <SummaryCard
                                                icon={PendingIcon}
                                                title="Pending"
                                                count={statusCounts['Pending'] || 0}
                                                status="Pending"
                                                color="#ff9800"
                                            />
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={6} md={2}>
                                            <SummaryCard
                                                icon={AccessTimeIcon}
                                                title="In Review"
                                                count={statusCounts['In Review'] || 0}
                                                status="In Review"
                                                color="#03a9f4"
                                            />
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={6} md={2}>
                                            <SummaryCard
                                                icon={ErrorOutlineIcon}
                                                title="Overdue"
                                                count={statusCounts['Overdue'] || 0}
                                                status="Overdue"
                                                color="#f44336"
                                            />
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={6} md={3}>
                                            <SummaryCard
                                                icon={CheckCircleIcon}
                                                title="Completed"
                                                count={statusCounts['Completed'] || 0}
                                                status="Completed"
                                                color="#4caf50"
                                            />
                                        </mui.Grid>
                                    </mui.Grid>
                                </mui.Box>

                                {error && (
                                    <mui.Alert severity="error" sx={{ mb: 3 }}>
                                        {error}
                                    </mui.Alert>
                                )}

                                <mui.Paper 
                                    sx={{ 
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        border: '1px solid rgba(148, 163, 184, 0.1)',
                                        overflow: 'hidden',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <DataGrid
                                        rows={filteredRequests}
                                        columns={columns}
                                        loading={loading}
                                        pageSize={10}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        checkboxSelection={false}
                                        disableSelectionOnClick
                                        autoHeight
                                        getRowId={(row) => row?.id || 0}
                                        slots={{
                                            toolbar: CustomGridToolbar
                                        }}
                                        initialState={{
                                            pagination: {
                                                pageSize: 10,
                                            },
                                            filter: {
                                                filterModel: {
                                                    items: [],
                                                    quickFilterValues: [],
                                                },
                                            },
                                        }}
                                        sx={{
                                            border: 'none',
                                            width: '100%',
                                            '& .MuiDataGrid-main': {
                                                borderRadius: 0,
                                                width: '100%'
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                marginTop: '56px !important'
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f8fafc',
                                                minHeight: '56px !important',
                                                maxHeight: '56px !important',
                                                lineHeight: '56px',
                                                borderBottom: '1px solid rgba(148, 163, 184, 0.1)'
                                            },
                                            '& .MuiDataGrid-columnHeader': {
                                                backgroundColor: '#f8fafc',
                                                height: '56px !important',
                                                maxHeight: '56px !important',
                                                '&:focus': {
                                                    outline: 'none'
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9'
                                                }
                                            },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                color: 'text.primary'
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                                                padding: '8px 16px'
                                            },
                                            '& .MuiDataGrid-row': {
                                                '&:hover': {
                                                    backgroundColor: '#f8fafc'
                                                }
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                borderTop: '1px solid rgba(148, 163, 184, 0.1)'
                                            },
                                            '& .MuiDataGrid-toolbarContainer': {
                                                padding: 0,
                                                borderBottom: 'none'
                                            }
                                        }}
                                    />
                                </mui.Paper>
                            </>
                        )}
                    </mui.Paper>
                </mui.Box>
            </mui.Box>
            <CustomSpeedDial
                actions={[
                    {
                        icon: <AddIcon />,
                        name: 'Create New Request',
                        onClick: () => setOpen(true)
                    }
                ]}
            />
            <mui.Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '90vh'
                    }
                }}
            >
                <mui.DialogTitle sx={{ 
                    pb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon color="primary" />
                        <mui.Typography variant="h6">Create New Request</mui.Typography>
                    </mui.Box>
                    <mui.IconButton
                        edge="end"
                        onClick={() => setOpen(false)}
                        aria-label="close"
                        size="small"
                    >
                        <CloseIcon />
                    </mui.IconButton>
                </mui.DialogTitle>
                <mui.DialogContent sx={{ 
                    py: 2,
                    px: 3,
                    backgroundColor: '#f8fafc'
                }}>
                    {formContent}
                </mui.DialogContent>
                <mui.DialogActions sx={{ 
                    p: 2.5,
                    backgroundColor: '#fff',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}>
                    <mui.Button
                        onClick={() => setOpen(false)}
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </mui.Button>
                    <mui.Button
                        variant="contained"
                        onClick={handleCreateRequest}
                        disabled={loading}
                        sx={{ 
                            minWidth: 120,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {loading ? (
                            <mui.CircularProgress size={24} />
                        ) : (
                            'Create Request'
                        )}
                    </mui.Button>
                </mui.DialogActions>
            </mui.Dialog>
            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
        </ResponsiveContainer>
    );

    return (
        <React.Suspense fallback="Loading...">
            <div>
                <IASideBar mainContent={customMainContent} />
            </div>
        </React.Suspense>
    );
}

export default AuditRequests;